import * as THREE from 'three'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import MotionBlur from '../postprocessing/MotionBlur'

/**
 * Owns the single renderer + full-screen canvas, the motion blur every frame goes through,
 * and the environment map that lights the PBR materials (the records).
 * The canvas lives outside the Swup container so it persists across page loads.
 * It's absolute, not fixed (iOS toolbars), and follow() moves it to the scroll each frame.
 */
export default class WebGLContext {
	constructor() {
		if (WebGLContext.instance) return WebGLContext.instance

		this.bleed = window.matchMedia('(pointer: coarse)').matches ? 160 : 0 // px past the viewport top and bottom
		this.canvas = this.#createCanvas()

		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: false, alpha: false })
		this.renderer.setClearColor(0x000000, 0)
		this.motionBlur = new MotionBlur(this.renderer)
		this.environment = WebGLContext.#environment(this.renderer)
		this.resize()

		WebGLContext.instance = this
	}

	/** Move the canvas over the viewport for this frame. */
	follow(scrollY) {
		this.canvas.style.transform = `translate3d(0, ${scrollY}px, 0)`
	}

	get width() {
		return this.canvas.clientWidth
	}

	get height() {
		return this.canvas.clientHeight
	}

	resize() {
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
		this.renderer.setSize(this.width, this.height, false) // false: leave the CSS size alone
		this.motionBlur.resize()
	}

	/** @param {Iterable<THREE.Object3D>} [jumped]  objects moved without animating: not blurred this frame */
	render(scene, camera, delta, jumped) {
		this.motionBlur.render(scene, camera, delta, jumped)
	}

	/** A neutral studio environment: soft area lights for the vinyl to reflect. */
	static #environment(renderer) {
		const pmrem = new THREE.PMREMGenerator(renderer)
		const texture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
		pmrem.dispose()
		return texture
	}

	#createCanvas() {
		const canvas = document.createElement('canvas')
		canvas.dataset.webgl = ''
		const bleed = `${this.bleed}px`
		Object.assign(canvas.style, {
			position: 'absolute',
			top: `-${bleed}`,
			left: 0,
			width: '100%',
			height: `calc(100vh + 2 * ${bleed})`,
			zIndex: 0,
			pointerEvents: 'none',
			willChange: 'transform',
		})
		canvas.style.height = `calc(100lvh + 2 * ${bleed})` // keeps 100vh where lvh is unsupported

		const frame = document.createElement('div')
		frame.dataset.webglFrame = ''
		Object.assign(frame.style, {
			position: 'absolute',
			inset: 0,
			overflow: 'clip',
			zIndex: 0,
			pointerEvents: 'none',
		})
		frame.append(canvas)
		document.body.prepend(frame)
		return canvas
	}
}
